import React, { useState, useCallback } from "react";

// @ts-ignore
import download from "downloadjs";

import { style } from "typestyle";
import SrsSelector from "./SrsSelector";
import type { ISRS, SrsName, SrsPair } from "../types";
import Button from "../../../components/Button";
import { useDropzone } from "react-dropzone";
import { convertCsv } from "../api";
import Alert from "../../../components/Alert";
import Card from "./Card";

interface MatrixCalculatorCsvProps {
  srs: ISRS,
}

export default function MatrixCalculatorCsv({ srs }: MatrixCalculatorCsvProps) {
  const srsSet: SrsName[] = srs.assumedSrs
    ? ["assumed", "global", "wgs84"]
    : ["global", "wgs84"];

  const [srsPair, setSrsPair] = useState<SrsPair>([srsSet[0], srsSet[1]]);
  const [error, setError] = useState("");

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError("");

      const csvFile = acceptedFiles[0];
      const reader = new FileReader();

      reader.onabort = () => setError("file reading was aborted");
      reader.onerror = () => setError("file reading has failed");
      reader.onload = () => {
        try {
          const csvContent = reader.result;
          if (csvContent == null) {
            throw Error("Failed to read csv document");
          }

          const convertedCsvContent = convertCsv(
            srsPair,
            srs,
            csvContent.toString(),
          );
          const csvFileNameWithoutExtension = csvFile.name
            .split(".")
            .slice(0, -1)
            .join(".");
          download(
            convertedCsvContent,
            `${csvFileNameWithoutExtension}_${srsPair[1]}.csv`,
            "text/csv",
          );
        } catch (e) {
          setError(e.message);
        }
      };

      acceptedFiles.forEach(file => reader.readAsBinaryString(file));
    },
    [srsPair, srs, setError],
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
  });

  return (
    <Card>
      <SrsSelector srsSet={srsSet} srsPair={srsPair} onChange={setSrsPair} />

      <div
        className={style({
          fontStyle: "normal",
          fontWeight: "normal",
          fontSize: "14px",
          lineHeight: "20px",
          color: "#64646E",
          display: "flex",
          justifyContent: "center",
        })}
      >
        Upload CSV file (point name, northing, easting, elevation). The
        converted file will be automatically downloaded.
      </div>

      {error && <Alert>{error}</Alert>}

      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <Button type="primary" size="big" label="Upload CSV" />
      </div>
    </Card>
  );
}
